import React, { useCallback, useEffect, useRef, useState } from "react";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import ListGroup from 'react-bootstrap/ListGroup';
import Spinner from 'react-bootstrap/Spinner';
import Stack from 'react-bootstrap/Stack';
import { Redirect } from 'react-router-dom';
import { patientsService } from '../../services/patients';
import { canAccessEvolutions } from '../../utils/access';
import { formatDateDisplay, formatDateForInput, formatDateTimeDisplay } from '../../utils/date';
import { getErrorMessage } from '../../utils/errorMessage';
import { filterNamedPatients } from '../../utils/records';
import './index.css';

const Evolution = () => {
    const acesso = Number(sessionStorage.getItem("tipoAcesso") || 0);
    const canOpenEvolution = canAccessEvolutions(acesso);
    const mountedRef = useRef(false);
    const messageTimerRef = useRef(null);
    const historyRequestRef = useRef(0);
    const [patients, setPatients] = useState([]);
    const [selectedPatientId, setSelectedPatientId] = useState('');
    const [selectedPatientName, setSelectedPatientName] = useState('');
    const [evolutions, setEvolutions] = useState([]);
    const [loadingPatients, setLoadingPatients] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingEvolutionId, setEditingEvolutionId] = useState(null);
    const [show, setShow] = useState(false);
    const [message, setMessage] = useState('');
    const todayDate = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        dataSessao: todayDate,
        risco: 'baixo',
        evolucao: '',
        conduta: '',
        planoTerapeutico: ''
    });

    const showMessage = (text) => {
        if (!mountedRef.current) {
            return;
        }

        clearTimeout(messageTimerRef.current);
        setMessage(text);
        setShow(true);
        messageTimerRef.current = setTimeout(() => {
            if (mountedRef.current) {
                setShow(false);
            }
        }, 7000);
    };

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            clearTimeout(messageTimerRef.current);
            historyRequestRef.current += 1;
        };
    }, []);

    const loadHistory = useCallback(async (patientId) => {
        const requestId = historyRequestRef.current + 1;
        historyRequestRef.current = requestId;

        if (!patientId) {
            if (mountedRef.current) {
                setEvolutions([]);
            }
            return;
        }

        try {
            if (mountedRef.current) {
                setLoadingHistory(true);
            }

            const response = await patientsService.getEvolutions(patientId);

            if (!mountedRef.current || historyRequestRef.current !== requestId) {
                return;
            }

            setEvolutions(response.data || []);
        } catch (error) {
            if (mountedRef.current && historyRequestRef.current === requestId) {
                showMessage(getErrorMessage(error, 'Nao foi possivel carregar o historico clinico.'));
            }
        } finally {
            if (mountedRef.current && historyRequestRef.current === requestId) {
                setLoadingHistory(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!canOpenEvolution) {
            setLoadingPatients(false);
            return undefined;
        }

        const fetchPatients = async () => {
            try {
                const response = await patientsService.getAssigned();
                const list = filterNamedPatients(response.data || []);

                if (!mountedRef.current) {
                    return;
                }

                setPatients(list);

                if (list.length > 0) {
                    setSelectedPatientId(String(list[0].idPacientes));
                    setSelectedPatientName(list[0].nome);
                    await loadHistory(list[0].idPacientes);
                }
            } catch (error) {
                if (mountedRef.current) {
                    showMessage(getErrorMessage(error, 'Nao foi possivel carregar os pacientes autorizados.'));
                }
            } finally {
                if (mountedRef.current) {
                    setLoadingPatients(false);
                }
            }
        };

        fetchPatients();
        return undefined;
    }, [canOpenEvolution, loadHistory]);

    if (!canOpenEvolution) {
        return <Redirect to="/" />;
    }

    const handlePatientChange = async (event) => {
        const patientId = event.target.value;
        const patient = patients.find((item) => String(item.idPacientes) === patientId);

        setSelectedPatientId(patientId);
        setSelectedPatientName(patient ? patient.nome : '');
        setEditingEvolutionId(null);
        setForm({
            dataSessao: todayDate,
            risco: 'baixo',
            evolucao: '',
            conduta: '',
            planoTerapeutico: ''
        });
        await loadHistory(patientId);
    };

    const resetForm = () => {
        setEditingEvolutionId(null);
        setForm({
            dataSessao: todayDate,
            risco: 'baixo',
            evolucao: '',
            conduta: '',
            planoTerapeutico: ''
        });
    };

    const handleSubmit = async () => {
        try {
            if (!selectedPatientId) {
                showMessage('Selecione um paciente.');
                return;
            }

            if (!form.dataSessao) {
                showMessage('Informe a data da sessao.');
                return;
            }

            if (form.dataSessao > todayDate) {
                showMessage('Nao e permitido registrar evolucao com data futura.');
                return;
            }

            if (!form.evolucao || form.evolucao.trim().length < 10) {
                showMessage('A evolucao clinica precisa ter pelo menos 10 caracteres.');
                return;
            }

            setSaving(true);

            if (editingEvolutionId) {
                await patientsService.patchEvolution(selectedPatientId, editingEvolutionId, form);
                await loadHistory(selectedPatientId);
                showMessage('Evolucao atualizada com sucesso.');
            } else {
                await patientsService.postEvolution(selectedPatientId, form);
                await loadHistory(selectedPatientId);
                showMessage('Evolucao registrada com sucesso.');
            }

            resetForm();
        } catch (error) {
            showMessage(getErrorMessage(error, 'Nao foi possivel salvar a evolucao.'));
        } finally {
            if (mountedRef.current) {
                setSaving(false);
            }
        }
    };

    const handleEdit = (item) => {
        setEditingEvolutionId(item.idEvolucao);
        setForm({
            dataSessao: formatDateForInput(item.dataSessao, todayDate),
            risco: item.risco || 'baixo',
            evolucao: item.evolucao || '',
            conduta: item.conduta || '',
            planoTerapeutico: item.planoTerapeutico || ''
        });
    };

    const handleDelete = async (item) => {
        try {
            if (!selectedPatientId || !item?.idEvolucao) {
                return;
            }

            if (!window.confirm('Deseja remover esta evolucao?')) {
                return;
            }

            setSaving(true);
            await patientsService.deleteEvolution(selectedPatientId, item.idEvolucao);
            await loadHistory(selectedPatientId);

            if (editingEvolutionId === item.idEvolucao) {
                resetForm();
            }

            showMessage('Evolucao removida com sucesso.');
        } catch (error) {
            showMessage(getErrorMessage(error, 'Nao foi possivel remover a evolucao.'));
        } finally {
            if (mountedRef.current) {
                setSaving(false);
            }
        }
    };

    return (
        <Container fluid className="evolution-page">
            <div className="Alert">
                <Alert show={show} variant="info" dismissible onClose={() => setShow(false)}>
                    {message}
                </Alert>
            </div>

            <Row className="mb-4">
                <Col>
                    <h2>Evolucao clinica</h2>
                    <p className="evolution-subtitle">Historico sensivel carregado sob demanda, sem armazenamento local permanente.</p>
                </Col>
            </Row>

            <Row>
                <Col lg={4} className="mb-4">
                    <Card className="evolution-card">
                        <Card.Body>
                            <Card.Title>Paciente</Card.Title>
                            {loadingPatients ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" />
                                </div>
                            ) : (
                                <>
                                    <Form.Select value={selectedPatientId} onChange={handlePatientChange}>
                                        <option value="">Selecione um paciente</option>
                                        {patients.map((patient) => (
                                            <option key={patient.idPacientes} value={patient.idPacientes}>
                                                {patient.nome}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <div className="patient-meta">
                                        <strong>Paciente atual:</strong> {selectedPatientName || 'Nenhum selecionado'}
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="evolution-card">
                        <Card.Body>
                            <Card.Title>{editingEvolutionId ? 'Editar evolucao' : 'Nova evolucao'}</Card.Title>
                            <Form.Group className="mb-3">
                                <Form.Label>Data da sessao</Form.Label>
                                <Form.Control
                                    max={todayDate}
                                    type="date"
                                    value={form.dataSessao}
                                    onChange={(event) => setForm({ ...form, dataSessao: event.target.value })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Nivel de risco</Form.Label>
                                <Form.Select
                                    value={form.risco}
                                    onChange={(event) => setForm({ ...form, risco: event.target.value })}
                                >
                                    <option value="baixo">Baixo</option>
                                    <option value="moderado">Moderado</option>
                                    <option value="alto">Alto</option>
                                    <option value="urgente">Urgente</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Evolucao clinica</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    value={form.evolucao}
                                    onChange={(event) => setForm({ ...form, evolucao: event.target.value })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Conduta</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={form.conduta}
                                    onChange={(event) => setForm({ ...form, conduta: event.target.value })}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Plano terapeutico</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={form.planoTerapeutico}
                                    onChange={(event) => setForm({ ...form, planoTerapeutico: event.target.value })}
                                />
                            </Form.Group>
                            <Button variant="success" onClick={handleSubmit} disabled={saving}>
                                {saving ? 'Salvando...' : (editingEvolutionId ? 'Atualizar evolucao' : 'Salvar evolucao')}
                            </Button>
                            {editingEvolutionId ? (
                                <Button className="ms-2" variant="secondary" onClick={resetForm} disabled={saving}>
                                    Cancelar edicao
                                </Button>
                            ) : null}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8}>
                    <Card className="evolution-card">
                        <Card.Body>
                            <Card.Title>Historico</Card.Title>
                            {loadingHistory ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" />
                                </div>
                            ) : (
                                <ListGroup variant="flush">
                                    {evolutions.length ? evolutions.map((item) => (
                                        <ListGroup.Item key={item.idEvolucao} className="evolution-history-item">
                                            <div className="history-header">
                                                <div>
                                                    <strong>{formatDateDisplay(item.dataSessao)}</strong>
                                                    <span>{item.profissional} | Risco {item.risco}</span>
                                                </div>
                                                <Stack direction="horizontal" gap={2}>
                                                    <Button
                                                        disabled={saving}
                                                        onClick={() => handleEdit(item)}
                                                        size="sm"
                                                        variant="outline-primary"
                                                    >
                                                        Editar
                                                    </Button>
                                                    <Button
                                                        disabled={saving}
                                                        onClick={() => handleDelete(item)}
                                                        size="sm"
                                                        variant="outline-danger"
                                                    >
                                                        Apagar
                                                    </Button>
                                                </Stack>
                                            </div>
                                            <p><strong>Evolucao:</strong> {item.evolucao}</p>
                                            {item.conduta ? <p><strong>Conduta:</strong> {item.conduta}</p> : null}
                                            {item.planoTerapeutico ? <p><strong>Plano:</strong> {item.planoTerapeutico}</p> : null}
                                            <small>Registrado em {formatDateTimeDisplay(item.criadoEm)}</small>
                                        </ListGroup.Item>
                                    )) : (
                                        <ListGroup.Item>Nenhuma evolucao registrada para este paciente.</ListGroup.Item>
                                    )}
                                </ListGroup>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Evolution;
